import * as crypto from 'crypto';
import forge from 'node-forge';

export const idStringLength = 16; // Must be even to allow the combining of ids
export const tokenStringLength = 22;

export const idRegexString = `^[0-9a-zA-Z]{${idStringLength}}$`;
export const tokenRegexString = `^[0-9a-zA-Z]{${tokenStringLength}}$`;

export const zeroId = '0'.repeat(idStringLength);
export const oneId = '1'.repeat(idStringLength);

export const nameMaxLength = 50;
export const orgNameMaxLength = 50;
export const tagNameMaxLength = 50;

export const addressLineMaxLength = 100;
export const addressCityMaxLength = 50;
export const addressStateMaxLength = 50;
export const addressPostalCodeMaxLength = 20;

export const taxIdMaxLength = 25;

/**
 * Creates a cryptographically secure ID of fixed number of characters.
 */
export function generateId(): string {
  // The Mongo _id is 24 characters. We've used 16 characters to be different.
  // This gives about (10+26+26)^16 = 4.73E28
  return convertBase64ToId(forge.util.encode64(forge.random.getBytesSync(32)));
}

function convertBase64ToId(value: string) {
  return value
    .split('/')
    .join('')
    .split('+')
    .join('')
    .split('=')
    .join('')
    .substring(0, idStringLength);
}

export function directMessageId(str1: string, str2: string) {
  const str1Sub = str1.substring(0, 8);
  const str2Sub = str2.substring(0, 8);
  return str1Sub < str2Sub ? str1Sub + str2Sub : str2Sub + str1Sub;
}

export function isLikelyDirectId({
  userId,
  threadId,
}: {
  userId: string;
  threadId: string;
}) {
  return (
    threadId.startsWith(userId.substring(0, 8)) ||
    threadId.endsWith(userId.substring(0, 8))
  );
}

export function determineDirectUserId({
  userId,
  userIds,
  threadId,
}: {
  userId: string;
  userIds: string[];
  threadId: string;
}) {
  return userIds.find((u) => directMessageId(userId, u) === threadId);
}

/**
 * Creates a cryptographically secure token of fixed number of characters.
 */
export function generateToken(): string {
  return forge.util
    .encode64(forge.random.getBytesSync(32))
    .split('/')
    .join('')
    .split('+')
    .join('')
    .split('=')
    .join('')
    .substring(0, tokenStringLength); // approx 2.7E39 of entropy
}

export function hashHex(text: string): string {
  const md = forge.md.sha256.create();
  md.update(text);
  return md.digest().toHex();
}

export function hash64(text: string): string {
  const md = forge.md.sha256.create();
  md.update(text);
  return forge.util.encode64(md.digest().getBytes());
}

export const confirmEmailCodeLength = 15;
export const confirmEmailCodeRegexString = `^[0-9]{${confirmEmailCodeLength}}$`;
export function generateConfirmEmailCode() {
  const array = new Uint32Array(confirmEmailCodeLength);
  crypto.getRandomValues(array);
  const code = array.join('').substring(0, confirmEmailCodeLength);
  return code;
}

export function generatePath<T extends { [key: string]: string | number }>(
  pathString: string,
  params: T
): string {
  if (pathString.startsWith('http://') || pathString.startsWith('https://')) {
    throw new Error('generatePath only works with paths, not full URIs');
  }
  const pathArray = pathString.split('/');

  const pathKeys = pathArray
    .filter((e) => e.startsWith(':'))
    .map((e) => e.substring(1));

  const paramsKeys = Object.keys(params);

  const missingPathKeys = paramsKeys.filter((e) => !pathKeys.includes(e));
  const missingParamsKeys = pathKeys.filter((e) => !paramsKeys.includes(e));

  if (missingPathKeys.length || missingParamsKeys.length) {
    throw new Error(
      'Path string and params do not line up: ' +
        JSON.stringify({ pathString, params })
    );
  }

  const newPathArray = pathArray.map((e) => {
    if (!e.startsWith(':')) return e;
    const key = e.substring(1);
    return params[key];
  });
  const path = newPathArray.join('/');
  return path;
}

export function pathMatchParams<T>(
  pathTemplate: string,
  path: string
): T | void {
  const p = path.split('?')[0].split('#')[0];

  if (pathTemplate.includes('?'))
    throw new Error("pathTemplate should not include '?'");
  if (p.startsWith('http://') || p.startsWith('https://'))
    throw new Error("Do not start path with 'http://' or 'https://'");

  const templateSegments = pathTemplate.split('/').map((segment) => ({
    value: segment.startsWith(':') ? segment.substring(1) : segment,
    isParameter: segment.startsWith(':'),
  }));

  const pathSegments = path.split('/');

  if (pathSegments.length !== templateSegments.length) return; // not a match for this template

  const obj: any = {};
  for (let i = 0; i < templateSegments.length; i++) {
    if (templateSegments[i].isParameter) {
      obj[templateSegments[i].value] = pathSegments[i];
    } else {
      if (templateSegments[i].value !== pathSegments[i]) return; // not a match for this template
    }
  }
  return obj;
}

export function generateIdFromOrgId(orgId: string) {
  const salt = generateId();
  const _id = hashSaltedOrgId({ orgId, salt });
  return { salt, _id };
}

/**
 * Determines whether a user-created _id is specific to this orgId to prevent
 * _id conflicts between organizations.
 */
export function validateId({
  orgId,
  _id,
  salt,
}: {
  orgId: string;
  _id: string;
  salt: string;
}) {
  return hashSaltedOrgId({ orgId, salt }) === _id;
}

function hashSaltedOrgId({ orgId, salt }: { orgId: string; salt: string }) {
  return convertBase64ToId(hash64(orgId + salt));
}

export function hashConfirmEmailCode({
  confirmEmailCode,
  email,
}: {
  confirmEmailCode: string;
  email: string;
}) {
  return convertBase64ToId(hash64(confirmEmailCode + ' ' + email));
}

export function sortCaseInsensitive(a: string, b: string) {
  if (a.toLowerCase() > b.toLowerCase()) return 1;
  if (a.toLowerCase() < b.toLowerCase()) return -1;
  return 0;
}

export function sortByNameExactFirst(inputValue: string) {
  const value = cleanForFilter(inputValue);
  return (aInput: { name: string }, bInput: { name: string }) => {
    const a = cleanForFilter(aInput.name);
    const b = cleanForFilter(bInput.name);
    if (a.startsWith(value) && !b.startsWith(value)) return -1;
    if (!a.startsWith(value) && b.startsWith(value)) return 1;

    return a < b ? -1 : a === b ? 0 : 1;
  };
}

export function sortByTitleExactFirst(inputValue: string) {
  const value = cleanForFilter(inputValue);
  return (aInput: { title: string }, bInput: { title: string }) => {
    const a = cleanForFilter(aInput.title);
    const b = cleanForFilter(bInput.title);
    if (a.startsWith(value) && !b.startsWith(value)) return -1;
    if (!a.startsWith(value) && b.startsWith(value)) return 1;

    return a < b ? -1 : a === b ? 0 : 1;
  };
}

export function cleanForFilter(value: string) {
  return value.trim().split(' ').join().toLowerCase();
}

export function cleanTitleExceptTrim(title: string): string {
  return title.replace('\n', '').replace('\r', '').replace('\t', '');
}

export function cleanTitle(title: string): string {
  return cleanTitleExceptTrim(title).trim();
}

export function spaceChannelTitle(spaceName: string) {
  let name = spaceName.trim();
  if (!name.endsWith(' space') && !name.endsWith(' Space')) name += ' Space';
  return name;
}

export const loginRouteConfirmEmailErrorMessage = 'Confirm email.';
export const inviteExpiredErrorMessage = 'Invite expired';

export function sleep(ms: number) {
  return new Promise((res) => setTimeout(res, ms));
}

export const systemUserId = '0000000000000000';
